import { APP_BASE_ROUTE, APP_CHECKOUT_ROUTE } from "utils/routes";

const CHECKOUT_PLAN_STORAGE_KEY = "tradez-payment-plan";
const CHECKOUT_PLAN_SLUG_STORAGE_KEY = "tradez-payment-plan-slug";
const CHECKOUT_PLAN_NAME_STORAGE_KEY = "tradez-payment-plan-name";
const CHECKOUT_EMAIL_STORAGE_KEY = "tradez-payment-email";
const PLAN_SLUG_ALIASES = {
  "pro-anual": "proanual",
  "pro-annual": "proanual",
};

const PLAN_INTENT_CONTENT = {
  gratis: {
    slug: "gratis",
    tierId: 1,
    name: "Grátis",
    checkoutEnabled: false,
    authTitle: "Crie sua conta e comece a organizar patrimônio, caixa e metas com clareza",
    authDescription:
      "Comece grátis para reunir ativos, receitas, despesas, orçamento e metas em um só lugar.",
    dashboardTitle: "Comece grátis e coloque ordem no patrimônio e no mês",
    dashboardDescription:
      "Monte sua base no app em poucos minutos, acompanhe patrimônio, orçamento e metas com mais clareza e faça upgrade só quando quiser ir além.",
    dashboardHighlights: [
      "Organize patrimônio, receitas, despesas, orçamento e metas em um só painel.",
      "Ganhe visibilidade sobre saldo do mês, limites e exposição antes de aumentar risco.",
      "Mude de plano apenas quando quiser remover limites ou destravar mais poder de decisão na carteira.",
    ],
  },
  standard: {
    slug: "standard",
    tierId: 2,
    name: "Standard",
    checkoutEnabled: true,
    authTitle: "Crie sua conta e deixe o Standard pronto para remover limites da sua base",
    authDescription:
      "Entre agora, organize seu patrimônio no grátis e deixe tudo pronto para remover limites de ativos e lançamentos sem perder o plano que você escolheu.",
    dashboardTitle: "Seu plano Standard está a um passo de liberar mais fôlego",
    dashboardDescription:
      "Monte sua base no app primeiro. Quando quiser remover limites e acompanhar mais volume sem fricção, ative o Standard em poucos cliques.",
    dashboardHighlights: [
      "Tudo o que já existe no grátis continua disponível.",
      "Acompanhe mais ativos e mais lançamentos sem fricção.",
      "Ative o Standard para remover limites e ganhar mais fôlego no dia a dia.",
    ],
  },
  pro: {
    slug: "pro",
    tierId: 3,
    name: "Pro",
    checkoutEnabled: true,
    authTitle: "Crie sua conta e deixe o Pro pronto para liberar a camada avançada da carteira",
    authDescription:
      "Entre agora, organize patrimônio, orçamento e metas no grátis e avance para o Pro quando quiser usar rebalanceamento e simulador de alocação.",
    dashboardTitle: "Seu próximo salto de clareza financeira e de alocação está no Pro",
    dashboardDescription:
      "Sua base já pode começar agora. Quando quiser somar rebalanceamento e simulador de alocação à rotina que você já montou no grátis, finalize o checkout do Pro.",
    dashboardHighlights: [
      "Tudo o que já existe no grátis continua com você.",
      "Destrave o rebalanceamento para corrigir concentração com mais confiança.",
      "Use o simulador de alocação para redistribuir capital sem improviso.",
    ],
  },
  proanual: {
    slug: "proanual",
    tierId: 4,
    name: "Pro Anual",
    checkoutEnabled: true,
    authTitle: "Crie sua conta e deixe o Pro Anual pronto para um ano inteiro de camada Pro",
    authDescription:
      "Entre agora, organize sua carteira no grátis e ative o Pro Anual quando quiser garantir 12 meses de rebalanceamento e simulação.",
    dashboardTitle: "Seu Pro Anual está pronto para virar clareza e disciplina ao longo do ano",
    dashboardDescription:
      "Comece a montar sua base agora e finalize o Pro Anual quando quiser manter a camada Pro de alocação ativa pelos próximos 12 meses.",
    dashboardHighlights: [
      "Leve a mesma camada Pro com um compromisso anual.",
      "Reduza o custo médio para manter rebalanceamento e simulador ao longo do ano.",
      "Evite reavaliar a assinatura todo mês enquanto consolida a rotina.",
    ],
  },
};

const canUseStorage = () => typeof window !== "undefined" && Boolean(window.localStorage);

export const normalizeCheckoutPlanSlug = (planSlug) => {
  if (!planSlug) {
    return null;
  }

  const normalizedSlug = String(planSlug).trim().toLowerCase();
  return PLAN_SLUG_ALIASES[normalizedSlug] || normalizedSlug;
};

export const persistCheckoutPlan = (plan) => {
  if (!canUseStorage() || !plan) {
    return;
  }

  if (plan.tierId != null) {
    window.localStorage.setItem(CHECKOUT_PLAN_STORAGE_KEY, String(plan.tierId));
  }

  if (plan.slug) {
    window.localStorage.setItem(CHECKOUT_PLAN_SLUG_STORAGE_KEY, normalizeCheckoutPlanSlug(plan.slug));
  }

  if (plan.name) {
    window.localStorage.setItem(CHECKOUT_PLAN_NAME_STORAGE_KEY, plan.name);
  }
};

export const persistCheckoutEmail = (email) => {
  if (!canUseStorage()) {
    return;
  }

  if (!email) {
    window.localStorage.removeItem(CHECKOUT_EMAIL_STORAGE_KEY);
    return;
  }

  window.localStorage.setItem(CHECKOUT_EMAIL_STORAGE_KEY, email.trim().toLowerCase());
};

export const getStoredCheckoutEmail = () => {
  if (!canUseStorage()) {
    return null;
  }

  return window.localStorage.getItem(CHECKOUT_EMAIL_STORAGE_KEY);
};

export const getStoredCheckoutPlanId = () => {
  if (!canUseStorage()) {
    return null;
  }

  return window.localStorage.getItem(CHECKOUT_PLAN_STORAGE_KEY);
};

export const getStoredCheckoutPlanSlug = () => {
  if (!canUseStorage()) {
    return null;
  }

  return normalizeCheckoutPlanSlug(window.localStorage.getItem(CHECKOUT_PLAN_SLUG_STORAGE_KEY));
};

export const getStoredCheckoutPlanName = () => {
  if (!canUseStorage()) {
    return null;
  }

  return window.localStorage.getItem(CHECKOUT_PLAN_NAME_STORAGE_KEY);
};

export const clearCheckoutIntent = ({ preserveEmail = false } = {}) => {
  if (!canUseStorage()) {
    return;
  }

  window.localStorage.removeItem(CHECKOUT_PLAN_STORAGE_KEY);
  window.localStorage.removeItem(CHECKOUT_PLAN_SLUG_STORAGE_KEY);
  window.localStorage.removeItem(CHECKOUT_PLAN_NAME_STORAGE_KEY);

  if (!preserveEmail) {
    window.localStorage.removeItem(CHECKOUT_EMAIL_STORAGE_KEY);
  }
};

export const getCheckoutPlanFromSearch = (search = "") => {
  const params = new URLSearchParams(search);
  return normalizeCheckoutPlanSlug(params.get("plan") || params.get("plano"));
};

export const resolveCheckoutPlanSlug = (search = "") => {
  return getCheckoutPlanFromSearch(search) || getStoredCheckoutPlanSlug();
};

export const buildPaymentRoute = (planSlug, extraParams = {}) => {
  const params = new URLSearchParams();
  const normalizedPlanSlug = normalizeCheckoutPlanSlug(planSlug);

  if (normalizedPlanSlug) {
    params.set("plan", normalizedPlanSlug);
  }

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value != null && value !== "") {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${APP_CHECKOUT_ROUTE}?${queryString}` : APP_CHECKOUT_ROUTE;
};

export const buildAuthRoute = (basePath, planSlug, extraParams = {}) => {
  const params = new URLSearchParams();
  const normalizedPlanSlug = normalizeCheckoutPlanSlug(planSlug);

  if (normalizedPlanSlug) {
    params.set("plan", normalizedPlanSlug);
  }

  Object.entries(extraParams).forEach(([key, value]) => {
    if (value != null && value !== "") {
      params.set(key, String(value));
    }
  });

  const queryString = params.toString();
  return queryString ? `${basePath}?${queryString}` : basePath;
};

export const getPlanIntentContent = (planSlug) => {
  const normalizedPlanSlug = normalizeCheckoutPlanSlug(planSlug);
  if (!normalizedPlanSlug) {
    return null;
  }

  return PLAN_INTENT_CONTENT[normalizedPlanSlug] || null;
};

export const resolvePostAuthPath = ({ search = "", forceAppHome = false }) => {
  if (forceAppHome) {
    return APP_BASE_ROUTE;
  }

  const params = new URLSearchParams(search);
  const shouldGoToDashboard = params.get("afterPayment") === "1";
  const shouldUpgradeNow = params.get("upgradeNow") === "1";
  const checkoutPlanSlug = getCheckoutPlanFromSearch(search);
  const planIntent = getPlanIntentContent(checkoutPlanSlug);

  if (shouldGoToDashboard) {
    return APP_BASE_ROUTE;
  }

  if (shouldUpgradeNow && planIntent?.checkoutEnabled) {
    return buildPaymentRoute(planIntent.slug);
  }

  return APP_BASE_ROUTE;
};
